/**
 * Vercel AI SDK provider configuration for the XAES Analyst chat.
 *
 * Wraps `streamText` from `ai` with the Anthropic provider so the rest
 * of the feature can ask for a token stream without thinking about
 * SDK plumbing. When `VITE_ANTHROPIC_API_KEY` is unset, returns a
 * mock async iterable so the panel still feels alive in local dev.
 *
 * Source references:
 *   - Loveable v1 reference: `src/lib/claude-api.ts` — same role but
 *     used the `@anthropic-ai/sdk` direct. ADR 0001 mandates the
 *     Vercel AI SDK abstraction; this file is the migration target.
 *   - AI SDK Anthropic provider docs — `createAnthropic({ apiKey })`
 *     and `streamText({ model, messages, abortSignal })`.
 *   - ADR 0001 — cache-control on the system block is set in
 *     `context-builder.ts`; this file just passes it through.
 */

import { createAnthropic } from "@ai-sdk/anthropic";
import { streamText, type CoreMessage } from "ai";
import { env, isClaudeConfigured } from "@/env";

/**
 * Args for {@link streamXaesReply}. Kept narrow on purpose — the
 * caller (the chat hook) is the only consumer and it never needs the
 * full `streamText` surface.
 */
export type StreamXaesReplyArgs = {
  /** Pre-composed system prompt string (see context-builder). */
  system: string;
  /** Conversation turns so far — must start with a `user` turn. */
  messages: CoreMessage[];
  /** Cancels the in-flight request on unmount or user-initiated abort. */
  signal: AbortSignal;
  /** Anthropic model id; default is set by the caller. */
  model: string;
};

/** Mock-mode timing. Chosen so the panel feels responsive but reads
 *  clearly as "not a real reply" to the user. */
const MOCK_DELAY_MS = 1500;

/** Generate the canned mock reply. Echoes the first 80 chars of the
 *  most recent user turn so the demo doesn't look frozen. */
function buildMockReply(messages: CoreMessage[]): string {
  // Walk from the back to find the latest user turn — assistant turns
  // can appear before it in the history when retry is invoked.
  let lastUserText = "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || m.role !== "user") continue;
    if (typeof m.content === "string") {
      lastUserText = m.content;
    } else if (Array.isArray(m.content)) {
      const textPart = m.content.find((p) => p.type === "text");
      lastUserText =
        textPart && "text" in textPart ? (textPart.text as string) : "";
    }
    break;
  }
  const snippet = lastUserText.slice(0, 80);
  return (
    `🤖 _Mock mode_ — set \`VITE_ANTHROPIC_API_KEY\` to talk to Claude.\n\n` +
    `You said: _"${snippet}${lastUserText.length > 80 ? "…" : ""}"_\n\n` +
    `When the key is configured I'll stream a real answer here, grounded in the workshop context and any \`@\`-mentions you attached.`
  );
}

/** Async generator that yields the mock reply in two chunks so the
 *  caller's streaming code path still exercises. */
async function* mockStream(reply: string, signal: AbortSignal): AsyncIterable<string> {
  // Initial "thinking" delay so the UI's status pill has time to
  // render "thinking" before tokens land.
  await sleep(MOCK_DELAY_MS, signal);
  if (signal.aborted) return;
  // First half, then a brief gap, then the rest — exercises the
  // delta-append logic in the chat hook.
  const mid = Math.floor(reply.length / 2);
  yield reply.slice(0, mid);
  await sleep(120, signal);
  if (signal.aborted) return;
  yield reply.slice(mid);
}

/** Abort-aware `setTimeout`. Resolves on either elapsed time or abort. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const id = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Stream tokens from Claude (or the mock fallback). The returned
 * iterable yields raw text deltas; the caller appends them to the
 * in-flight assistant bubble.
 *
 * Aborts are non-fatal — both the SDK and the mock stream stop yielding
 * once the signal trips, and the caller is expected to handle the
 * resulting truncated message.
 */
export function streamXaesReply({
  system,
  messages,
  signal,
  model,
}: StreamXaesReplyArgs): AsyncIterable<string> {
  const apiKey = env.VITE_ANTHROPIC_API_KEY;
  if (!isClaudeConfigured() || !apiKey) {
    return mockStream(buildMockReply(messages), signal);
  }

  // The Anthropic provider reads the key from its constructor. We pass
  // the value explicitly so the browser bundle doesn't try to look up
  // `process.env.ANTHROPIC_API_KEY` (which doesn't exist client-side).
  const anthropic = createAnthropic({
    apiKey,
    // Browser-direct mode. Treat the key as PUBLIC — see env.ts.
    headers: { "anthropic-dangerous-direct-browser-access": "true" },
  });

  // AI SDK v4: `system` is a top-level parameter (string). It is NOT
  // accepted as a `role: "system"` entry in `messages` — the schema
  // there only allows user / assistant / tool roles.
  const result = streamText({
    model: anthropic(model),
    system,
    messages,
    abortSignal: signal,
  });

  // Use `fullStream` instead of `textStream`: the latter silently
  // swallows API errors (auth, model-not-found, malformed request) in
  // v4 — you get an empty stream and no throw. `fullStream` surfaces
  // an `error` part we can re-throw, letting the chat hook's catch
  // block render the message.
  return iterateFullStream(result);
}

async function* iterateFullStream(result: ReturnType<typeof streamText>): AsyncIterable<string> {
  for await (const part of result.fullStream) {
    if (part.type === "text-delta") {
      yield part.textDelta;
    } else if (part.type === "error") {
      const err = part.error;
      // Surface the raw error so the chat hook's catch block can
      // render the message. The AI SDK wraps non-Error throwables, so
      // unwrap to a friendly string when possible.
      if (err instanceof Error) throw err;
      if (typeof err === "object" && err !== null && "message" in err) {
        throw new Error(String((err as { message: unknown }).message));
      }
      throw new Error(String(err));
    }
    // Other part types (`finish`, `tool-call`, `tool-result`,
    // `step-start`, `step-finish`, etc.) are noise for this consumer.
  }
}
